/**
 * Route Component Map - Maps module IDs to their page components
 * 
 * SOLID Principles:
 * - Single Responsibility: Each component handles one page
 * - Open/Closed: Add new routes without modifying existing code
 * - Dependency Inversion: Depends on abstractions (ComponentType) not concrete implementations
 */

import { ComponentType, lazy } from 'react'

// Lazy load pages for better performance
// Dashboard
const Home = lazy(() => import('../pages/dashboard/Home'))
const UsersAnalytics = lazy(() => import('../pages/dashboard/UsersAnalytics'))
const Usage = lazy(() => import('../pages/dashboard/Usage'))

// Accounts
const Accounts = lazy(() => import('../pages/accounts/Accounts'))

// Subscriptions
const MySubscriptions = lazy(() => import('../pages/subscriptions/MySubscriptions'))

// Allegro - Offers
const AllegroOfferEditor = lazy(() => import('../pages/allegro/offers/OfferEditor'))
const AllegroCopyOffers = lazy(() => import('../pages/allegro/offers/CopyOffers'))
const AllegroProductCards = lazy(() => import('../pages/allegro/offers/ProductCards'))
const AllegroTitles = lazy(() => import('../pages/allegro/offers/Titles'))
const AllegroTitlesV2 = lazy(() => import('../pages/allegro/offers/TitlesV2'))
const AllegroOpisomat = lazy(() => import('../pages/allegro/offers/Opisomat'))
const AllegroParameters = lazy(() => import('../pages/allegro/offers/Parameters'))
const AllegroComprehensiveFix = lazy(() => import('../pages/allegro/offers/ComprehensiveFix'))
const AllegroListingFromScratch = lazy(() => import('../pages/allegro/offers/ListingFromScratch'))
const AllegroOfferManagement = lazy(() => import('../pages/allegro/offers/OfferManagement'))
const AllegroOpinionAlerts = lazy(() => import('../pages/allegro/offers/OpinionAlerts'))
const AllegroAccountQualityAlerts = lazy(() => import('../pages/allegro/offers/AccountQualityAlerts'))
const AllegroSellerRatingAlerts = lazy(() => import('../pages/allegro/offers/SellerRatingAlerts'))

// Allegro - Images
const AllegroImages = lazy(() => import('../pages/allegro/images/Images'))
const AllegroSavedImages = lazy(() => import('../pages/allegro/images/SavedImages'))
const AllegroGrafikomat = lazy(() => import('../pages/allegro/images/Grafikomat'))

// Allegro - Pricing
const AllegroPriceScheduler = lazy(() => import('../pages/allegro/pricing/PriceScheduler'))
const AllegroFlexibleBundles = lazy(() => import('../pages/allegro/flexible-bundles/FlexibleBundles'))
const AllegroMultipacks = lazy(() => import('../pages/allegro/multipacks/Multipacks'))

// Allegro - Ads
const AllegroAdsHome = lazy(() => import('../pages/allegro/ads/AdsHome'))

// Decathlon - Offers
const DecathlonCreateOffer = lazy(() => import('../pages/decathlon/offers/CreateOffer'))

// Castorama - Offers
const CastoramaCreateOffer = lazy(() => import('../pages/castorama/offers/CreateOffer'))

// Leroy Merlin - Offers
const LeroyMerlinCreateOffer = lazy(() => import('../pages/leroymerlin/offers/CreateOffer'))

// eMAG
const EmagComingSoon = lazy(() => import('../pages/emag/ComingSoon'))
const EmagOpisomat = lazy(() => import('../pages/emag/offers/Opisomat'))
const EmagTytulomat = lazy(() => import('../pages/emag/offers/Tytulomat'))
const EmagListingFromAllegro = lazy(() => import('../pages/emag/offers/ListingFromAllegro'))

// School
const SchoolPage = lazy(() => import('../pages/school/SchoolPage'))

// Credits
const CreditsPage = lazy(() => import('../pages/credits/CreditsPage'))
const ReferralProgramPage = lazy(() => import('../pages/referrals/ReferralProgramPage'))
const AdminCreditsPage = lazy(() => import('../pages/admin/credits/AdminCreditsPage'))

// Admin & Profile
const AdminUsers = lazy(() => import('../pages/admin/AdminUsers'))
const SimpleUserManagement = lazy(() => import('../pages/admin/SimpleUserManagement'))
const AdminAIPrompts = lazy(() => import('../pages/admin/AdminAIPrompts'))
const AdminOpisomatConfig = lazy(() => import('../pages/admin/AdminOpisomatConfig'))
const AdminReferralsPage = lazy(() => import('../pages/admin/AdminReferralsPage'))
const AdminAccountingPage = lazy(() => import('../pages/admin/AdminAccountingPage'))
const AdminSiteNotice = lazy(() => import('../pages/admin/AdminSiteNotice'))
const AdminAdsAgency = lazy(() => import('../pages/admin/AdminAdsAgency'))
const AdminFeatureSurveys = lazy(() => import('../pages/admin/AdminFeatureSurveys'))
const AdminOnboardingAnalytics = lazy(() => import('../pages/admin/AdminOnboardingAnalytics'))
const AdminGrafikomatFeedback = lazy(() => import('../pages/admin/AdminGrafikomatFeedback'))
const GrafikomatPipelinePreview = lazy(() => import('../pages/dev/GrafikomatPipelinePreview'))
const Profile = lazy(() => import('../pages/profile/Profile'))
const AIConfig = lazy(() => import('../pages/profile/AIConfig'))

/**
 * Component map - Maps module IDs to their React components
 * This is the single source of truth for route -> component mapping
 */
export const ROUTE_COMPONENT_MAP: Record<string, ComponentType> = {
  // Core
  'dashboard': Home,
  'konta_marketplace': Accounts,
  'subscriptions': MySubscriptions,
  'profil': Profile,
  'ai_config': AIConfig,
  
  // Allegro
  'allegro_edytor_ofert': AllegroOfferEditor,
  'allegro_wystawianie_od_zera': AllegroListingFromScratch,
  'allegro_kopiowanie_ofert': AllegroCopyOffers,
  'allegro_zestawy_elastyczne': AllegroFlexibleBundles,
  'allegro_wielopaki': AllegroMultipacks,
  'allegro_harmonogram_cen': AllegroPriceScheduler,
  'allegro_tytuly': AllegroTitles,
  'allegro_tytuly_v2': AllegroTitlesV2,
  'allegro_grafikomat': AllegroGrafikomat,
  'allegro_opisomat': AllegroOpisomat,
  'allegro_parametry': AllegroParameters,
  'allegro_kompleksowe': AllegroComprehensiveFix,
  'allegro_zarzadzanie_ofertami': AllegroOfferManagement,
  'allegro_alerty_opinie': AllegroOpinionAlerts,
  'allegro_alerty_jakosc': AllegroAccountQualityAlerts,
  'allegro_alerty_oceny_konta': AllegroSellerRatingAlerts,
  'allegro_ads': AllegroAdsHome,
  'allegro_karty_produktowe': AllegroProductCards,
  'allegro_dodawanie_grafik': AllegroImages,
  'allegro_zapisane_zdjecia': AllegroSavedImages,
  
  // Decathlon
  'decathlon_wystawianie_ofert': DecathlonCreateOffer,
  
  // Castorama
  'castorama_wystawianie_ofert': CastoramaCreateOffer,
  
  // Leroy Merlin
  'leroymerlin_wystawianie_ofert': LeroyMerlinCreateOffer,

  // eMAG
  'emag_coming_soon': EmagComingSoon,
  'emag_opisomat': EmagOpisomat,
  'emag_tytulomat': EmagTytulomat,
  'emag_wystawianie_ofert': EmagListingFromAllegro,

  // General
  'zuzycie_ai': Usage,
  'credits': CreditsPage,
  'referrals': ReferralProgramPage,
  'szkola_ofertatora': SchoolPage,

  // Admin (special routes, not in module registry)
  'admin_credits': AdminCreditsPage,
  'admin_users': AdminUsers,
  'simple_user_management': SimpleUserManagement,
  'admin_ai_prompts': AdminAIPrompts,
  'admin_opisomat_config': AdminOpisomatConfig,
  'admin_referrals': AdminReferralsPage,
  'admin_accounting': AdminAccountingPage,
  'admin_site_notice': AdminSiteNotice,
  'admin_ads_agency': AdminAdsAgency,
  'admin_feature_surveys': AdminFeatureSurveys,
  'admin_onboarding_analytics': AdminOnboardingAnalytics,
  'admin_grafikomat_feedback': AdminGrafikomatFeedback,
  'dev_grafikomat_pipeline_preview': GrafikomatPipelinePreview,
  'users_analytics': UsersAnalytics,
}

/**
 * Get component for a module ID
 */
export const getComponentForModule = (moduleId: string): ComponentType | undefined => {
  return ROUTE_COMPONENT_MAP[moduleId]
}

/**
 * Check if a module has a registered component
 */
export const hasRegisteredComponent = (moduleId: string): boolean => {
  return moduleId in ROUTE_COMPONENT_MAP
}
